import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";

import api from "../api";
import { useMasterData } from "../../utils/useMasterData";

function DailyTransactionLog() {
  const { getValues } = useMasterData("SOURCE_TYPE");
  const sourceTypes = ["", ...getValues("SOURCE_TYPE")];
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 8)}01`;

  const [view, setView] = useState("MONTH");
  const [filters, setFilters] = useState({
    date_from: monthStart,
    date_to: today,
    account_code: "",
    source_type: "",
  });
  const [rows, setRows] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        view,
        ...(view === "ALL" ? filters : {
          account_code: filters.account_code,
          source_type: filters.source_type,
          date_from: filters.date_from,
          date_to: filters.date_to,
        }),
      });
      const res = await api.get(`/accounts/ledger/daily-transactions?${query.toString()}`);
      setRows(res.data.data || []);
      setAccountOptions(res.data.account_options || []);
      setTotals({
        debit: Number(res.data.total_debits || 0),
        credit: Number(res.data.total_credits || 0),
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load daily transactions.");
    } finally {
      setLoading(false);
    }
  }, [filters, view]);

  const loadLedger = useCallback(async () => {
    if (!filters.account_code) {
      setLedger(null);
      return;
    }
    try {
      const query = new URLSearchParams({
        date_from: filters.date_from,
        date_to: filters.date_to,
      });
      const res = await api.get(`/accounts/ledger/account/${filters.account_code}?${query.toString()}`);
      setLedger(res.data);
    } catch {
      setLedger(null);
    }
  }, [filters.account_code, filters.date_from, filters.date_to]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const isBalanced = useMemo(() => Math.abs(totals.debit - totals.credit) < 0.01, [totals]);

  const exportCsv = () => {
    const headers = ["Date", "Entry No", "Description", "Account", "Source Type", "Debit", "Credit", "Narration"];
    const lines = rows.map((row) => [
      row.date,
      row.entry_no,
      row.description,
      `${row.account_code} ${row.account_name ? `- ${row.account_name}` : ""}`.trim(),
      row.source_type,
      row.debit,
      row.credit,
      row.narration || "",
    ]);
    const csv = [headers, ...lines]
      .map((cols) => cols.map((value) => `"${String(value ?? "").replaceAll(`"`, `""`)}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `daily-transactions-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Daily Transaction Log</h3>
            <p className="text-sm text-slate-500">Filter posted journal lines by date range, account, and source type.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["DAY", "WEEK", "MONTH", "ALL"].map((tab) => (
              <button key={tab} onClick={() => setView(tab)} className={`tab-btn ${view === tab ? "tab-btn-active" : ""}`}>{tab}</button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 border-b border-slate-100 px-6 py-4 md:grid-cols-5">
          <input type="date" value={filters.date_from} onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))} className="input" />
          <input type="date" value={filters.date_to} onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))} className="input" />
          <select value={filters.account_code} onChange={(e) => setFilters((prev) => ({ ...prev, account_code: e.target.value }))} className="input">
            <option value="">All Accounts</option>
            {accountOptions.map((account) => (
              <option key={account.account_code} value={account.account_code}>{account.account_code} - {account.account_name}</option>
            ))}
          </select>
          <select value={filters.source_type} onChange={(e) => setFilters((prev) => ({ ...prev, source_type: e.target.value }))} className="input">
            {sourceTypes.map((type) => <option key={type || "all"} value={type}>{type || "All Sources"}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            <button onClick={load} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
            <button onClick={exportCsv} className="secondary-btn"><Download size={15} /> Export CSV</button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Entry No</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Debit</th>
                <th className="px-4 py-3">Credit</th>
                <th className="px-4 py-3">Narration</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.map((row, index) => (
                <tr key={`${row.entry_no}-${index}`} className="border-t border-slate-100">
                  <td className="px-4 py-3">{row.date}</td>
                  <td className="px-4 py-3 font-semibold text-indigo-700">{row.entry_no}</td>
                  <td className="px-4 py-3">{row.description}</td>
                  <td className="px-4 py-3">{row.account_code} {row.account_name ? `- ${row.account_name}` : ""}</td>
                  <td className="px-4 py-3"><SourceBadge sourceType={row.source_type} /></td>
                  <td className="px-4 py-3 font-semibold text-emerald-700">Rs. {Number(row.debit || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 font-semibold text-rose-700">Rs. {Number(row.credit || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">{row.narration || "-"}</td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-500">No posted transactions found.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="8" className="px-4 py-10 text-center text-slate-500">Loading transactions...</td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td colSpan="5" className="px-4 py-3 font-semibold text-slate-700">Totals</td>
                <td className="px-4 py-3 font-bold text-emerald-700">Rs. {totals.debit.toFixed(2)}</td>
                <td className="px-4 py-3 font-bold text-rose-700">Rs. {totals.credit.toFixed(2)}</td>
                <td className={`px-4 py-3 text-sm font-semibold ${isBalanced ? "text-emerald-700" : "text-rose-700"}`}>
                  {isBalanced ? "Balanced" : "Out of Balance"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {ledger && (
        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-5">
            <h4 className="text-lg font-bold text-slate-900">{ledger.account_code} - {ledger.account_name}</h4>
            <p className="text-sm text-slate-500">Opening Rs. {Number(ledger.opening_balance || 0).toFixed(2)} | Closing Rs. {Number(ledger.closing_balance || 0).toFixed(2)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Entry No</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Debit</th>
                  <th className="px-4 py-3">Credit</th>
                  <th className="px-4 py-3">Balance</th>
                </tr>
              </thead>
              <tbody>
                {(ledger.entries || []).map((entry, index) => (
                  <tr key={`${entry.entry_no}-${index}`} className="border-t border-slate-100">
                    <td className="px-4 py-3">{entry.date}</td>
                    <td className="px-4 py-3 font-semibold text-indigo-700">{entry.entry_no}</td>
                    <td className="px-4 py-3">{entry.description}</td>
                    <td className="px-4 py-3 text-emerald-700">Rs. {Number(entry.debit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-rose-700">Rs. {Number(entry.credit || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold">Rs. {Number(entry.balance || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn, .tab-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
        .tab-btn-active {
          background: rgb(79 70 229);
          border-color: rgb(79 70 229);
          color: white;
        }
      `}</style>
    </div>
  );
}

function SourceBadge({ sourceType }) {
  const palette = {
    SALES_INVOICE: "bg-emerald-100 text-emerald-700",
    METAL_EXCHANGE: "bg-amber-100 text-amber-700",
    PAYROLL: "bg-sky-100 text-sky-700",
    MANUAL: "bg-slate-100 text-slate-700",
    PURCHASE: "bg-fuchsia-100 text-fuchsia-700",
    INVENTORY: "bg-indigo-100 text-indigo-700",
  };
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${palette[sourceType] || palette.MANUAL}`}>{sourceType || "MANUAL"}</span>;
}

export default DailyTransactionLog;
